import { useCallback, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import { MixedAudioRecorder } from '@src/services';
import SocketService from '@src/services/SocketService';

import {
  usePeersStore,
  useRecordingStore,
  useRoomActionsStore,
  useAudioStreamStore,
  useRoomStore,
  useVideoStreamStore,
} from '@src/stores';

import { getDataTimeString } from '@src/utils';

import { ECommonSocketKeys } from '@src/types';

const useWebinarRecording = () => {
  const { currentRecorder } = useRecordingStore(
    useShallow((state) => ({
      currentRecorder: state.currentRecorder,
    }))
  );

  const { updatePeerInfo } = usePeersStore(
    useShallow((state) => ({
      isHostUser: state.isHostUser,
      updatePeerInfo: state.updatePeerInfo,
    }))
  );

  const {
    screenRecordingSettings: { isActive: isScreenRecordingActive },
  } = useRoomActionsStore(
    useShallow((state) => ({
      screenRecordingSettings: state.screenRecordingSettings,
    }))
  );

  const {
    canUserRecord,
    currentPeer,
    isMobileDevice,
    recordedBlobsRef,
    audioRecorderRef,
    mediaRecorderRef,
    recScreenStreamRef,
    isRecordingRef,
    isAudioEventListenerActiveRef,
  } = useRoomContext();

  const updatePeers = useCallback(
    (peerId: string, status: boolean) => {
      updatePeerInfo(peerId, { peer_recording: status });
      useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: status });
    },
    [updatePeerInfo]
  );

  const recordingAction = useCallback(
    (action: string) => {
      SocketService.emit(ECommonSocketKeys.RECORDING_ACTION, {
        peer_name: currentRecorder.peer_name || currentPeer?.peer_name || currentPeer?.peer_info.peer_name,
        peer_id: currentRecorder?.peer_id || currentPeer?.id || currentPeer?.peer_info?.peer_id,
        action,
        status: isRecordingRef.current,
        initiator_peer_id: currentPeer?.peer_info?.peer_id || currentPeer?.id,
      });
    },
    [
      currentPeer?.id,
      currentPeer?.peer_info?.peer_id,
      currentPeer?.peer_info.peer_name,
      currentPeer?.peer_name,
      currentRecorder?.peer_id,
      currentRecorder.peer_name,
      isRecordingRef,
    ]
  );

  const forceStopAllRecordings = useCallback(
    (skipBroadcast = false) => {
      if (!skipBroadcast) {
        recordingAction('Stop conference recording');
      }
      updatePeers(currentPeer?.id || '', false);
      useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: false });
      // Ensure any pending start flow is cleared so start-recording modal doesn't reopen
      useRecordingStore.getState().setCurrentRecorder({ peer_name: '', peer_id: '', action: '' });
    },
    [currentPeer?.id, recordingAction, updatePeers]
  );

  const stopRecording = useCallback(
    (skipPeerInfoUpdate = false) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (mediaRecorder) {
        isRecordingRef.current = false;

        updatePeers(currentPeer?.id || '', false);

        try {
          mediaRecorder.stop();
        } catch {
          // ignore if already stopped
        }
        mediaRecorderRef.current = null;

        const rec = recScreenStreamRef.current;
        if (rec) {
          rec.getTracks().forEach((t) => {
            t.stop();
          });
          recScreenStreamRef.current = null;
        }

        try {
          audioRecorderRef.current?.stopMixedAudioStream();
        } catch {
          // ignore
        }

        if (!skipPeerInfoUpdate) {
          forceStopAllRecordings();
        }
        useRecordingStore.getState().setCurrentRecorder({ peer_name: '', peer_id: '', action: '' });
      }
    },
    [
      audioRecorderRef,
      currentPeer?.id,
      forceStopAllRecordings,
      isRecordingRef,
      mediaRecorderRef,
      recScreenStreamRef,
      updatePeers,
    ]
  );

  const handleRecordingError = useCallback(
    (error: unknown, popupLog = true) => {
      console.warn('Recording error:', error);
      stopRecording(true);
    },
    [stopRecording]
  );

  const getSupportedMimeTypes = useCallback(() => {
    const possibleTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/mp4;codecs=h264,aac',
      'video/mp4',
    ];
    return possibleTypes.filter((mimeType) => {
      try {
        return MediaRecorder.isTypeSupported(mimeType);
      } catch {
        return false;
      }
    });
  }, []);

  const saveRecordingInLocalDevice = useCallback(
    (blob: Blob, recFileName: string, eventType?: string) => {
      if (eventType === 'stop') {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = recFileName;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          recordedBlobsRef.current = [];
        }, 100);
      }
      stopRecording();
    },
    [recordedBlobsRef, stopRecording]
  );

  const handleLocalRecordingStop = useCallback(
    (evt: Event) => {
      if (!isAudioEventListenerActiveRef.current) return;

      if (recordedBlobsRef.current.length === 0) {
        console.warn('No recording data available');
        stopRecording();
        return;
      }

      const dateTime = getDataTimeString();
      const firstType = (recordedBlobsRef.current[0] as any)?.type as string | undefined;
      const type = firstType?.includes('mp4') ? 'mp4' : 'webm';

      const blob = new Blob(recordedBlobsRef.current, { type: `video/${type}` });
      const recFileName = `Rec_${dateTime}.${type}`;
      saveRecordingInLocalDevice(blob, recFileName, (evt as any)?.type);
    },
    [isAudioEventListenerActiveRef, recordedBlobsRef, saveRecordingInLocalDevice, stopRecording]
  );

  const initRecording = useCallback(() => {
    isRecordingRef.current = true;

    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder) return;

    // Add error event listener
    const onError = (evt: any) => {
      console.error('MediaRecorder error:', evt.error);
      handleRecordingError(evt.error, false);
    };

    // dataavailable
    const onData = (evt: BlobEvent) => {
      if (evt.data && evt.data.size > 0) {
        recordedBlobsRef.current.push(evt.data);
      }
    };

    // stop/inactive -> single handler
    const onStop = (evt: Event) => {
      try {
        mediaRecorder.removeEventListener('error', onError);
        mediaRecorder.removeEventListener('dataavailable', onData);
        mediaRecorder.removeEventListener('stop', onStop);
        mediaRecorder.removeEventListener('inactive', onStop);

        handleLocalRecordingStop(evt);
      } catch (err) {
        console.error('Recording save failed', err);
      }
    };

    // start and wire listeners
    try {
      mediaRecorder.start(1000); // Capture data every second
      mediaRecorder.addEventListener('error', onError);
      mediaRecorder.addEventListener('dataavailable', onData);
      mediaRecorder.addEventListener('stop', onStop);
      mediaRecorder.addEventListener('inactive', onStop);

      // if user stops the screen share
      const screenTrack = recScreenStreamRef.current?.getVideoTracks()?.[0];
      if (screenTrack) {
        const onScreenEnded = (evt: Event) => onStop(evt);
        screenTrack.addEventListener('ended', onScreenEnded);
      }

      recordingAction('Start Meeting recording');
      updatePeers(currentPeer?.id || '', true);
    } catch (error) {
      handleRecordingError(error, false);
    }
  }, [
    currentPeer?.id,
    handleLocalRecordingStop,
    handleRecordingError,
    isRecordingRef,
    mediaRecorderRef,
    recScreenStreamRef,
    recordedBlobsRef,
    recordingAction,
    updatePeers,
  ]);

  const getAudioStreamFromAudioElements = useCallback(() => {
    const audioElements = useAudioStreamStore.getState().audioStreams;
    const audioStream = new MediaStream();
    audioElements.forEach(({ stream }: { stream: MediaStream }) => {
      if (stream.id !== 'avatarPreviewAudio') {
        const audioTracks = stream.getAudioTracks();
        audioTracks.forEach((track) => {
          if (track) audioStream.addTrack(track);
        });
      }
    });
    return audioStream;
  }, []);

  const presentDesktopConfirmation = useCallback((onConfirm: () => void, onDismiss: () => void) => {
    useRecordingStore.setState({
      onConfirm: () => {
        onConfirm();
        useRoomStore.getState().setIsInfoModalOpen(false);
        useRoomStore.getState().setInfoModalContent(null);
      },
      onDismiss: () => {
        onDismiss();
        useRoomStore.getState().setIsInfoModalOpen(false);
        useRoomStore.getState().setInfoModalContent(null);
      },
    });
  }, []);

  const beginDesktopFlow = useCallback(() => {
    const supported = getSupportedMimeTypes();
    if (!supported.length) {
      handleRecordingError('No supported MIME types available for recording', false);
      return;
    }

    const options: MediaRecorderOptions = {
      mimeType: supported[0] as MediaRecorderOptions['mimeType'],
    };

    presentDesktopConfirmation(
      async () => {
        try {
          // Get screen with audio
          const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
          });

          // Get microphone audio
          const micStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });

          // Get audio from app elements
          const appAudioStream = getAudioStreamFromAudioElements();

          // Combine all tracks
          const combinedTracks: MediaStreamTrack[] = [
            ...screenStream.getVideoTracks(),
            ...screenStream.getAudioTracks(),
            ...micStream.getAudioTracks(),
            ...appAudioStream.getAudioTracks(),
          ];

          const recScreenStream = new MediaStream(combinedTracks);

          recScreenStreamRef.current = recScreenStream;
          mediaRecorderRef.current = new MediaRecorder(recScreenStream, options);

          initRecording();
        } catch (err) {
          handleRecordingError('Unable to record the screen + audio: ' + err, false);
        }
      },
      () => {
        if (mediaRecorderRef.current) {
          stopRecording();
        } else {
          forceStopAllRecordings();
        }
      }
    );
  }, [
    forceStopAllRecordings,
    getAudioStreamFromAudioElements,
    getSupportedMimeTypes,
    handleRecordingError,
    initRecording,
    mediaRecorderRef,
    presentDesktopConfirmation,
    recScreenStreamRef,
    stopRecording,
  ]);

  const startRecording = useCallback(async () => {
    // reset chunks
    recordedBlobsRef.current = [];

    try {
      const supported = getSupportedMimeTypes();
      if (!supported.length) throw new Error('No supported MIME types available for recording.');

      const options: MediaRecorderOptions = {
        mimeType: supported[0] as MediaRecorderOptions['mimeType'],
      };

      // Ask for mic+cam
      const userStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });

      // MixedAudioRecorder
      audioRecorderRef.current = new MixedAudioRecorder(!!userStream);

      // pull all currently-playing audio tracks from your app
      const audioStreams = getAudioStreamFromAudioElements();

      const mixedAudioStream = audioRecorderRef.current.getMixedAudioStream([audioStreams]);

      // Combine user stream with mixed audio
      const combinedTracks: MediaStreamTrack[] = [...userStream.getVideoTracks(), ...mixedAudioStream.getAudioTracks()];

      new MediaStream(combinedTracks);

      // Desktop vs mobile flow
      if (isMobileDevice) {
        const originalStream = useVideoStreamStore.getState().originalStream;
        if (originalStream) {
          combinedTracks.push(...originalStream.getVideoTracks());
        }

        const recCamStream = new MediaStream(combinedTracks);
        mediaRecorderRef.current = new MediaRecorder(recCamStream, options);
        initRecording();
      } else {
        beginDesktopFlow();
      }
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: false });
        stopRecording(true);
        isAudioEventListenerActiveRef.current = false;
        return;
      }
      handleRecordingError(error, false);
    }
  }, [
    audioRecorderRef,
    beginDesktopFlow,
    getAudioStreamFromAudioElements,
    getSupportedMimeTypes,
    handleRecordingError,
    initRecording,
    isAudioEventListenerActiveRef,
    isMobileDevice,
    mediaRecorderRef,
    recordedBlobsRef,
    stopRecording,
  ]);

  const saveRecording = useCallback(
    (_reason?: string) => {
      if (isRecordingRef.current) stopRecording();
    },
    [isRecordingRef, stopRecording]
  );

  useEffect(() => {
    return () => {
      try {
        if (isRecordingRef.current) stopRecording(true);
      } catch {}
    };
  }, [isRecordingRef, stopRecording]);

  useEffect(() => {
    if (currentPeer?.peer_info && !currentPeer.peer_info.peer_recording && isScreenRecordingActive) {
      stopRecording();
    }
  }, [currentPeer?.peer_info, isScreenRecordingActive, stopRecording]);

  return {
    canUserRecord,
    startRecording,
    stopRecording,
    saveRecording,
    beginDesktopFlow,
    mediaRecorderRef,
    recScreenStreamRef,
    audioRecorderRef,
    forceStopAllRecordings,
  };
};

export default useWebinarRecording;
